import Vue from "vue";

import MissingCustomerNameModal from "@/shared/components/customer/MissingCustomerNameModal.vue";
import { Customer } from "@/shared/interfaces/customer/Customer.interface";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import {
    isCustomerHydrated,
    isPlaceholderCustomerName,
    requiresPersonName
} from "@/shared/scripts/helpers/customer/customer";

/**
 * IO-154: forces a usable customer name before an order is created.
 *
 * Creating the order is what emits the B2C log to Salesforce, and Salesforce rejects it with
 * REQUIRED_FIELD_MISSING: [Name] when the name is blank. The guard therefore has to run before
 * every `OrdersModule/createOrder` dispatch — guarding the payment click instead (as the first
 * attempt at IO-154 did) is too late, because the order already exists by then.
 *
 * Consume it as the base constructor, NOT through the `mixins: []` option:
 *
 *     export default missingCustomerNameMixin.extend({ ... });
 *
 * Vue 2's typings do not merge mixin members into `this` through the `mixins` option, so every
 * `this.blockOnMissingCustomerName(...)` call would fail the build's type check. Extending the
 * mixin constructor types cleanly and still allows a local `mixins: [other]` alongside it.
 *
 * Each consumer adds this block to its template:
 *
 *     <missing-customer-name-modal
 *         :is-open="showMissingCustomerNameModal"
 *         :is-saving="isSavingCustomerName"
 *         :initial-name="missingCustomerNameInitial"
 *         @save-name="saveMissingCustomerName"
 *         @close-modal="closeMissingCustomerNameModal"
 *     />
 */
export default Vue.extend({
    components: {
        MissingCustomerNameModal: MissingCustomerNameModal
    },

    data: function() {
        return {
            showMissingCustomerNameModal: false as boolean,
            isSavingCustomerName: false as boolean,
            pendingAfterCustomerName: null as (() => void) | null,
            isResolvingCustomerName: false as boolean,
            hydrationAttemptedForCustomerId: null as string | null
        };
    },

    computed: {
        missingCustomerNameCustomer: function(): Customer | null {
            return this.$store.state.CustomersModule.currentCustomer;
        },

        missingCustomerNameInitial: function(): string {
            const customer = this.missingCustomerNameCustomer;
            const name = customer && typeof customer.name === "string" ? customer.name : "";

            // Offer nothing rather than the placeholder applyCustomerNameFallback had to write to
            // satisfy the schema's minLength. Seeding "bogdan.mihaimosteanu-ext" into First name
            // makes the user delete it, and appending a surname to it would produce a name that
            // contains a space — passing every guard on its way to Salesforce.
            if (isPlaceholderCustomerName(name, this.$store.state.UserModule?.user?.email)) {
                return "";
            }

            return name;
        }
    },

    methods: {
        /**
         * Returns true when the caller must abort — either because the modal has been opened, or
         * because the real customer is being fetched first and `retry` will re-run the caller.
         *
         * Fails open (returns false) for anything it cannot safely repair, so it can never wedge
         * a funnel: a missing customer, a record that is still an `{ id }` placeholder after one
         * hydration attempt, or a company record (whose legal name the modal's full-replace PUT
         * would overwrite). Customers who have already paid are NOT excluded — see
         * requiresPersonName for why.
         */
        blockOnMissingCustomerName: function(retry: () => void): boolean {
            const customer = this.missingCustomerNameCustomer;

            if (!customer) {
                return false;
            }

            // Several flows commit a bare { id } before the customer GET resolves, so the name
            // is not yet knowable. Fetch the real record once, then re-enter the caller.
            if (!isCustomerHydrated(customer)) {
                // A hydration is already in flight. Keep blocking instead of falling open: no
                // consumer disables its button on this path, so a second click would otherwise
                // create an order with an unverified name AND leave the pending retry to create
                // a second one — two B2C logs from one publication.
                if (this.isResolvingCustomerName) {
                    return true;
                }

                // Latch per customer id rather than once per component lifetime: ProgressionWizard
                // outlives the whole classic funnel while the route guards keep committing fresh
                // { id } placeholders, so a lifetime-wide flag would disable recovery for the rest
                // of the session after a single failed GET.
                const customerId = customer.id;

                if (!customerId || this.hydrationAttemptedForCustomerId === customerId) {
                    return false;
                }

                this.hydrationAttemptedForCustomerId = customerId;
                this.isResolvingCustomerName = true;

                // Deliberately NOT cleared on success: if the GET returns a record that still
                // reads as unhydrated, the latch is what stops this from looping forever. The
                // next attempt then fails open, which is the documented behaviour.
                const resolved = () => {
                    this.isResolvingCustomerName = false;
                    retry();
                };

                this.$store.dispatch("CustomersModule/setCurrentCustomer", customerId)
                    .then(resolved)
                    .catch(resolved);

                return true;
            }

            if (!requiresPersonName(customer)) {
                return false;
            }

            this.pendingAfterCustomerName = retry;
            this.showMissingCustomerNameModal = true;

            return true;
        },

        /**
         * Backstop for the pre-flight rejection in `OrdersModule/createOrder`: turns that
         * rejection into the same modal instead of a generic error dialog. Returns true when the
         * errors were handled here and the caller should stop.
         */
        openModalOnMissingCustomerNameError: function(errors: unknown, retry: () => void): boolean {
            if (!Array.isArray(errors)) {
                return false;
            }

            // Matched narrowly on the pre-flight's own shape. A future backend validation error on
            // the name (too long, bad charset) would arrive on the same dataPath but is not
            // something this modal can repair, so it must fall through to the generic handler.
            const hasNameError = errors.some(function(error) {
                return !!error && error.dataPath === "customer.name" && error.keyword === "required";
            });

            if (!hasNameError) {
                return false;
            }

            this.pendingAfterCustomerName = retry;
            this.showMissingCustomerNameModal = true;

            return true;
        },

        saveMissingCustomerName: function(fullName: string): void {
            const customer = this.missingCustomerNameCustomer;

            // Without an id the PUT below would silently hit /customers/undefined. Unreachable
            // while the guard only fires on hydrated records, but say so rather than 404.
            if (!customer || !customer.id) {
                this.closeMissingCustomerNameModal();
                this.dialog.alert(i18n.t("create_customer_error").toString());
                return;
            }

            this.isSavingCustomerName = true;

            const ensureCustomerLoaded = isCustomerHydrated(customer)
                ? Promise.resolve(true)
                : this.$store.dispatch("CustomersModule/setCurrentCustomer", customer.id);

            ensureCustomerLoaded
                .then(() => {
                    return this.$store.dispatch("CustomersModule/updateCustomer", {
                        ...this.missingCustomerNameCustomer,
                        name: fullName
                    });
                })
                .then((saved: boolean) => {
                    this.isSavingCustomerName = false;

                    // updateCustomer RESOLVES false on a 4xx rather than rejecting, so the
                    // boolean is the only signal that the name was not persisted. Keep the modal
                    // open on failure so the typed values survive a retry, and never run the
                    // pending action — that is what let a blank name through before.
                    if (!saved) {
                        this.dialog.alert(i18n.t("create_customer_error").toString());
                        return;
                    }

                    const afterSave = this.pendingAfterCustomerName;
                    this.closeMissingCustomerNameModal();

                    if (afterSave) {
                        afterSave();
                    }
                })
                .catch((error: unknown) => {
                    this.isSavingCustomerName = false;
                    this.dialog.alert(i18n.t("server_not_responding_error").toString());
                    console.error(error);
                });
        },

        closeMissingCustomerNameModal: function(): void {
            this.showMissingCustomerNameModal = false;
            this.isSavingCustomerName = false;
            this.pendingAfterCustomerName = null;
        }
    }
});
